import React, { useEffect, useState, memo } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import Button from '../components/Button';
import ImageCarousel from '../components/ImageCarousel';
import { ArrowRightIcon, SparklesIcon, RocketIcon, BrainIcon, VideoIcon, TrendingUpIcon } from 'lucide-react';
const Home = () => {
  const {
    scrollYProgress
  } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);
  // Animated phrases
  const phrases = ['Convertimos ideas en proyectos funcionales con IA, creatividad y pasión', 'La Historia es Reina', 'No es como se ve, es como se siente', '¿Nuestro mejor trabajo? Es el siguiente'];
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhraseIndex(prev => (prev + 1) % phrases.length);
    }, 4000); // Change phrase every 4 seconds
    return () => clearInterval(interval);
  }, []);
  return <>
      {/* Hero Section - Full Screen */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-id="element-318">
        {/* Background Image with Parallax */}
        <motion.div className="absolute inset-0 z-0" style={{
        scale
      }} data-id="element-319">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 via-purple-900/80 to-gray-900" data-id="element-320"></div>
          <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=2072&q=80" alt="Technology Background" className="w-full h-full object-cover" data-id="element-321" />
        </motion.div>
        {/* Content */}
        <motion.div className="container mx-auto px-4 md:px-6 relative z-10 text-center" style={{
        opacity
      }} data-id="element-322">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }} data-id="element-323">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/weJPa2B6KxMjVxBcMFKGGT/LOGO_GRUPO_REFLEX.png" alt="Grupo Reflex Logo" className="h-32 md:h-40 mx-auto mb-8 brightness-0 invert" data-id="element-324" />
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.4
        }} className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-white leading-tight" data-id="element-325">
            Transformamos el futuro
            <br data-id="element-326" />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-id="element-327">
              con innovación
            </span>
          </motion.h1>
          {/* Animated Phrases */}
          <div className="h-24 md:h-32 flex items-center justify-center" data-id="element-328">
            <AnimatePresence mode="wait" data-id="element-329">
              <motion.p key={currentPhraseIndex} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.5
            }} className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto" data-id="element-330">
                {phrases[currentPhraseIndex]}
              </motion.p>
            </AnimatePresence>
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.8
        }} className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-331">
            <Button href="/proyectos" variant="primary" size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" data-id="element-332">
              Explorar proyectos
              <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-333" />
            </Button>
            <Button href="/contacto" variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-gray-900" data-id="element-334">
              Contactar
            </Button>
          </motion.div>
        </motion.div>
        {/* Scroll Indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2" data-id="element-335">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} className="text-white" data-id="element-336">
            <ArrowRightIcon className="h-8 w-8 rotate-90" data-id="element-337" />
          </motion.div>
        </motion.div>
      </section>
      {/* Image Carousel */}
      <ImageCarousel data-id="element-338" />
      {/* Services Section - Visual Cards */}
      <section className="py-32 bg-gray-900 relative overflow-hidden" data-id="element-339">
        <div className="absolute inset-0 opacity-10" data-id="element-340">
          <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl" data-id="element-341"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500 rounded-full blur-3xl" data-id="element-342"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-343">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} className="text-center mb-20" data-id="element-344">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" data-id="element-345">
              Servicios que{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-id="element-346">
                transforman
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-347">
              Soluciones diseñadas para el futuro
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-348">
            {[{
            title: 'Prototipos Innovadores',
            description: 'Convertimos conceptos en realidades funcionales con tecnología de vanguardia',
            icon: <RocketIcon className="h-8 w-8" data-id="element-349" />,
            image: 'https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
            gradient: 'from-purple-600/20 to-pink-600/20'
          }, {
            title: 'Inteligencia Artificial',
            description: 'Implementamos IA para optimizar procesos y crear experiencias únicas',
            icon: <BrainIcon className="h-8 w-8" data-id="element-350" />,
            image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
            gradient: 'from-blue-600/20 to-purple-600/20'
          }, {
            title: 'Producción Audiovisual',
            description: 'Contenido visual que cuenta historias impactantes y memorables',
            icon: <VideoIcon className="h-8 w-8" data-id="element-351" />,
            image: 'https://images.unsplash.com/photo-1574717024453-e599f3984a15?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
            gradient: 'from-pink-600/20 to-red-600/20'
          }, {
            title: 'Marketing Digital',
            description: 'Estrategias que potencian tu presencia y alcanzan tus objetivos',
            icon: <TrendingUpIcon className="h-8 w-8" data-id="element-352" />,
            image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
            gradient: 'from-green-600/20 to-blue-600/20'
          }].map((service, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 50
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className="group relative h-96 rounded-2xl overflow-hidden cursor-pointer" data-id="element-353">
                <div className="absolute inset-0" data-id="element-354">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-355" />
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} group-hover:opacity-90 transition-opacity duration-500`} data-id="element-356"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" data-id="element-357"></div>
                </div>
                <div className="relative h-full p-8 flex flex-col justify-end" data-id="element-358">
                  <div className="text-purple-400 mb-4 transform group-hover:scale-110 transition-transform duration-500" data-id="element-359">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3" data-id="element-360">
                    {service.title}
                  </h3>
                  <p className="text-gray-300" data-id="element-361">{service.description}</p>
                </div>
              </motion.div>)}
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} viewport={{
          once: true
        }} className="text-center mt-16" data-id="element-362">
            <Button href="/servicios" variant="primary" size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" data-id="element-363">
              Ver todos los servicios
            </Button>
          </motion.div>
        </div>
      </section>
      {/* Philosophy Section - Full Width Image */}
      <section className="relative h-screen flex items-center" data-id="element-364">
        <div className="absolute inset-0" data-id="element-365">
          <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Team collaboration" className="w-full h-full object-cover" data-id="element-366" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-purple-900/80 to-gray-900/95" data-id="element-367"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-368">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1
        }} viewport={{
          once: true
        }} className="max-w-2xl" data-id="element-369">
            <div className="flex items-center mb-6" data-id="element-370">
              <SparklesIcon className="h-8 w-8 text-purple-400 mr-3" data-id="element-371" />
              <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-372">
                Nuestra filosofía
              </h2>
            </div>
            <div className="space-y-8" data-id="element-373">
              <div data-id="element-374">
                <h3 className="text-2xl font-bold text-purple-400 mb-3" data-id="element-375">
                  No es como se ve... ¡Es como se siente!
                </h3>
                <p className="text-xl text-gray-300" data-id="element-376">
                  Creamos experiencias que trascienden lo visual y conectan
                  emocionalmente
                </p>
              </div>
              <div data-id="element-377">
                <h3 className="text-2xl font-bold text-pink-400 mb-3" data-id="element-378">
                  ¡La historia es Reina!
                </h3>
                <p className="text-xl text-gray-300" data-id="element-379">
                  Cada proyecto cuenta una historia única que resuena con tu
                  audiencia
                </p>
              </div>
              <div data-id="element-380">
                <h3 className="text-2xl font-bold text-purple-400 mb-3" data-id="element-381">
                  ¿Nuestro mejor trabajo? …¡El siguiente!
                </h3>
                <p className="text-xl text-gray-300" data-id="element-382">
                  Siempre buscamos superar nuestros límites y establecer nuevos
                  estándares
                </p>
              </div>
            </div>
            <div className="mt-12" data-id="element-383">
              <Button href="/historia" variant="primary" size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" data-id="element-384">
                Conoce nuestra historia
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Projects Showcase */}
      <section className="py-32 bg-gray-900" data-id="element-385">
        <div className="container mx-auto px-4 md:px-6" data-id="element-386">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} className="text-center mb-20" data-id="element-387">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" data-id="element-388">
              Proyectos que{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-id="element-389">
                inspiran
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-390">
              Historias de éxito que transforman industrias
            </p>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-391">
            {[{
            title: 'DingDong',
            category: 'App Design',
            image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1074&q=80'
          }, {
            title: 'Chiquimula Impresionante',
            category: 'Audiovisual',
            image: 'https://images.unsplash.com/photo-1574717024453-e599f3984a15?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80'
          }, {
            title: 'El Pickup hecho para tí',
            category: 'Campañas',
            image: 'https://images.unsplash.com/photo-1603575448878-868a20723f5d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80'
          }].map((project, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 50
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className="group relative h-96 rounded-2xl overflow-hidden cursor-pointer" data-id="element-392">
                <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-393" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" data-id="element-394"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-395">
                  <span className="text-sm font-semibold text-purple-400 uppercase tracking-wider" data-id="element-396">
                    {project.category}
                  </span>
                  <h3 className="text-3xl font-bold text-white mt-2" data-id="element-397">
                    {project.title}
                  </h3>
                </div>
              </motion.div>)}
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} viewport={{
          once: true
        }} className="text-center mt-16" data-id="element-398">
            <Button href="/proyectos" variant="primary" size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" data-id="element-399">
              Ver todos los proyectos
            </Button>
          </motion.div>
        </div>
      </section>
      {/* CTA Section - Full Screen */}
      <section className="relative h-screen flex items-center justify-center" data-id="element-400">
        <div className="absolute inset-0" data-id="element-401">
          <img src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Future technology" className="w-full h-full object-cover" data-id="element-402" />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-purple-900/90 to-gray-900/80" data-id="element-403"></div>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1
      }} viewport={{
        once: true
      }} className="container mx-auto px-4 md:px-6 text-center relative z-10" data-id="element-404">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white" data-id="element-405">
            ¿Listo para transformar
            <br data-id="element-406" />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-id="element-407">
              tu próximo proyecto?
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto" data-id="element-408">
            Estamos aquí para convertir tus ideas en realidad
          </p>
          <Button href="/contacto" variant="primary" size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg px-8 py-4" data-id="element-409">
            Comienza tu proyecto
            <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-410" />
          </Button>
        </motion.div>
      </section>
    </>;
};
export default Home;